/**
 * Extract a procedural-hold `BonePose` from a real animation clip.
 *
 * The procedural body system stores hold poses as Euler offsets in
 * bone-local space. Hand-authoring those offsets from scratch is hard
 * because each rig's local bone axes (which axis = swing, twist, raise)
 * are non-obvious. This module derives a first-pass offset table directly
 * from a Mixamo/Avaturn-compatible animation clip — the same kind of clip
 * the figure already plays through its mixer — so the resulting pose is
 * guaranteed to read correctly on the live character.
 *
 * ── How it works ──────────────────────────────────────────────────────────
 *
 * Procedural offsets compose with the mixer's pose like:
 *   final_rotation = mixer_pose + hold_offset    (when W=1, ignoring state/gesture)
 *
 * For a hold that we want to LOCK to "right hand behind head", we need:
 *   hold_offset = target_pose − mixer_anchor_pose
 *
 * Where:
 *   • `target_pose` = the rotation a real clip puts the bone in at some
 *     chosen frame (the moment in the clip where the actor's hand is
 *     behind their head).
 *   • `mixer_anchor_pose` = the rotation the *idle* clip puts the bone in
 *     at its frame 0 — used as a stable, canonical reference. We subtract
 *     this so the offset is "how far does the target deviate from rest"
 *     rather than absolute pose, matching the override system's semantics.
 *
 * Both poses are evaluated by reading the clip's KeyframeTrack data
 * directly (no mixer manipulation), which means this can run anytime
 * without disturbing the live figure on screen.
 *
 * ── Caveats ───────────────────────────────────────────────────────────────
 *
 * • Tracks store quaternions; we convert to Euler XYZ for delta math. For
 *   the small angles involved in our offsets this is a fine approximation
 *   and matches the system's existing additive-Euler composition. For
 *   large rotations (>~2 rad on multiple axes) Euler-subtraction would
 *   drift; for our seated-pose-style targets the delta stays well below
 *   that threshold per axis.
 *
 * • The clip's bone tracks must be Mixamo-named (or normalised to such).
 *   Modern Mixamo clips and Mixamo-compatible exports both qualify.
 *
 * ── Console usage ─────────────────────────────────────────────────────────
 *
 *   await __findClips('hands behind head')      // returns matches
 *   await __extractPose({                       // returns BonePose, prints code
 *     clip: './models/josh/anims/Right%20Hand%20Behind%20Head.fbx',
 *     time: 1.5,                                // pose at this clip time
 *     mirror: false,                            // optional L↔R mirror
 *   })
 */

import { AnimationClip, Euler, Quaternion } from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import {
  ALL_BONE_SLOTS,
  BONE_PATTERNS,
  type BonePose,
  type BoneSlot,
  type EulerOffset,
} from '../motion/body-profiles';

const fbxLoader = new FBXLoader();

interface AnimsManifestEntry {
  name: string;
  url: string;
  bytes?: number;
}

/**
 * Loaded once, then cached. Kept module-private so the public helpers can
 * resolve clip names → URLs without the caller passing the manifest in.
 */
let animsManifestCache: AnimsManifestEntry[] | null = null;

async function loadAnimsManifest(): Promise<AnimsManifestEntry[]> {
  if (animsManifestCache) return animsManifestCache;
  const res = await fetch('./models/josh/anims.json');
  if (!res.ok) {
    throw new Error(`failed to load anims manifest: HTTP ${res.status}`);
  }
  const data = (await res.json()) as AnimsManifestEntry[];
  animsManifestCache = data;
  return data;
}

/**
 * Search the anims manifest for clips whose name matches `query` (case-
 * insensitive substring or regex). Use this to discover URLs for the
 * extractor.
 */
export async function findClips(
  query: string | RegExp,
): Promise<{ name: string; url: string }[]> {
  const manifest = await loadAnimsManifest();
  const re = typeof query === 'string'
    ? new RegExp(query.split(/\s+/).map((w) => `(?=.*${escapeRegex(w)})`).join(''), 'i')
    : query;
  return manifest
    .filter((e) => re.test(e.name))
    .map((e) => ({ name: e.name, url: e.url }));
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ── Track evaluation ─────────────────────────────────────────────────────

/**
 * Find the quaternion track for a given Mixamo bone in a clip, regardless
 * of whether the clip uses the `mixamorig:` namespace prefix or not. The
 * clip may also be Mixamo-prefixed without the colon (e.g. `mixamorigHips`)
 * which is what the runtime's normalizeMixamoTracks produces.
 */
function findQuaternionTrack(clip: AnimationClip, slot: BoneSlot) {
  const pattern = BONE_PATTERNS[slot];
  for (const track of clip.tracks) {
    const dot = track.name.indexOf('.');
    if (dot === -1) continue;
    const boneName = track.name.slice(0, dot);
    const prop = track.name.slice(dot + 1);
    if (prop !== 'quaternion') continue;
    // Strip optional `mixamorig:` or `mixamorig` prefix so the bone
    // matcher (which is anchored to the bare bone name) can hit.
    const stripped = boneName.replace(/^mixamorig:?/i, '');
    if (pattern.test(stripped)) return track;
  }
  return null;
}

/**
 * Evaluate a quaternion track at the given time. Tracks store interleaved
 * (x,y,z,w) values keyed by `times[]`. We linearly interpolate between
 * the two surrounding keyframes and re-normalise — sufficient for poses
 * that are roughly at rest within the clip. (If the chosen frame is mid-
 * gesture, sample slightly later/earlier.)
 */
function sampleQuaternionTrack(
  track: { times: ArrayLike<number>; values: ArrayLike<number> },
  timeSec: number,
  out: Quaternion,
): Quaternion {
  const times = track.times;
  const values = track.values;
  const n = times.length;
  if (n === 0) return out.identity();
  if (n === 1 || timeSec <= times[0]) {
    out.set(values[0], values[1], values[2], values[3]);
    return out.normalize();
  }
  if (timeSec >= times[n - 1]) {
    const i = (n - 1) * 4;
    out.set(values[i], values[i + 1], values[i + 2], values[i + 3]);
    return out.normalize();
  }
  // Linear search — clips have ≤200 keyframes, this is fine.
  let i = 0;
  while (i < n - 1 && times[i + 1] < timeSec) i++;
  const t0 = times[i];
  const t1 = times[i + 1];
  const f = (timeSec - t0) / (t1 - t0);
  const a = i * 4;
  const b = (i + 1) * 4;
  const qa = new Quaternion(values[a], values[a + 1], values[a + 2], values[a + 3]);
  const qb = new Quaternion(values[b], values[b + 1], values[b + 2], values[b + 3]);
  // Slerp gives correct quaternion interpolation; lerp+normalise would
  // drift on large rotations.
  out.copy(qa).slerp(qb, f);
  return out;
}

/**
 * Evaluate every BoneSlot in `clip` at `timeSec`, returning a map of
 * bone-local Euler XYZ rotations. Slots without a track in the clip are
 * omitted from the result; the delta-builder below treats them as "no
 * change relative to base".
 */
function evaluateClipPose(clip: AnimationClip, timeSec: number): Partial<Record<BoneSlot, Euler>> {
  const out: Partial<Record<BoneSlot, Euler>> = {};
  const q = new Quaternion();
  for (const slot of ALL_BONE_SLOTS) {
    const track = findQuaternionTrack(clip, slot);
    if (!track) continue;
    sampleQuaternionTrack(track, timeSec, q);
    out[slot] = new Euler().setFromQuaternion(q, 'XYZ');
  }
  return out;
}

// ── Pose extraction ──────────────────────────────────────────────────────

export interface ExtractPoseOpts {
  /** URL or short name (resolved via findClips) of the target clip. */
  clip: string;
  /** Time in seconds within the target clip where the desired pose is held. */
  time: number;
  /**
   * URL of the idle clip used as the canonical reference pose. Defaults
   * to the runtime's breathing/base-idle clip so deltas line up with the
   * mixer pose the override system anchors against.
   */
  baseClip?: string;
  /** Time within the base clip (default 0). */
  baseTime?: number;
  /**
   * Mirror the result across the body's sagittal plane after extraction.
   * Use when the source clip only shows one side of a symmetric pose
   * (e.g. `Right Hand Behind Head` mirrored to give both hands).
   * The mirror swaps L/R bone slots and negates the y/z components per
   * the same convention the tuning panel uses.
   */
  mirror?: 'LR' | 'RL';
  /** Restrict the result to specific slots (e.g. just the arm chains). */
  onlySlots?: BoneSlot[];
  /**
   * When false, suppress the diagnostic console.log output. The tuning
   * panel passes this so rapid scrub operations don't flood the console.
   * Default true (preserves console-driven UX from main.ts).
   */
  log?: boolean;
}

const DEFAULT_BASE_CLIP_URL = './models/josh/anims/Standing%20Idle.fbx';

const ARM_SLOTS_LEFT: BoneSlot[]  = ['clavicleL', 'upperArmL', 'forearmL', 'handL'];
const ARM_SLOTS_RIGHT: BoneSlot[] = ['clavicleR', 'upperArmR', 'forearmR', 'handR'];
const ARM_SLOTS_BOTH = [...ARM_SLOTS_LEFT, ...ARM_SLOTS_RIGHT];
const MIRROR_PAIRS: Array<[BoneSlot, BoneSlot]> = [
  ['clavicleL', 'clavicleR'],
  ['upperArmL', 'upperArmR'],
  ['forearmL',  'forearmR'],
  ['handL',     'handR'],
];

/** In-memory clip cache. FBX parsing is expensive (~50–200ms); caching
 * means scrubbing the time slider is instant after the first load. */
const clipCache = new Map<string, AnimationClip>();
/** In-flight promises: if two callers ask for the same URL simultaneously,
 * share the single in-flight fetch rather than duplicating the network request. */
const clipInFlight = new Map<string, Promise<AnimationClip>>();

async function loadClip(url: string): Promise<AnimationClip> {
  const resolved = url.includes('/') ? url : await resolveClipUrl(url);
  const cached = clipCache.get(resolved);
  if (cached) return cached;
  const existing = clipInFlight.get(resolved);
  if (existing) return existing;
  const promise = fbxLoader.loadAsync(resolved).then((obj) => {
    const raw = obj.animations[0];
    if (!raw) throw new Error(`clip at ${resolved} has no animations`);
    clipCache.set(resolved, raw);
    clipInFlight.delete(resolved);
    return raw;
  });
  clipInFlight.set(resolved, promise);
  return promise;
}

async function resolveClipUrl(nameOrFragment: string): Promise<string> {
  const matches = await findClips(nameOrFragment);
  if (matches.length === 0) {
    throw new Error(`no clip matched "${nameOrFragment}"`);
  }
  if (matches.length > 1) {
    console.warn(
      `[extract-pose] "${nameOrFragment}" matched ${matches.length} clips; using "${matches[0].name}"`,
    );
    matches.slice(1, 6).forEach((m) => console.warn(`  also: ${m.name}`));
  }
  return matches[0].url;
}

export interface ExtractPoseResult {
  /** Bone-local Euler offsets (delta vs base clip) ready for the override system. */
  pose: BonePose;
  /** Duration of the target clip in seconds — useful for time-slider bounds. */
  duration: number;
}

/**
 * Build a `BonePose` whose values are the Euler delta between
 * `target_clip(time)` and `base_clip(baseTime)`. The result is suitable
 * to drop into PROCEDURAL_HOLD_POSES as a first-pass — refine in the
 * tuning panel afterward.
 *
 * Returns the pose plus the loaded clip's duration so UI callers can
 * size a scrub slider without re-loading the clip themselves.
 */
export async function extractPose(opts: ExtractPoseOpts): Promise<ExtractPoseResult> {
  const baseClipUrl = opts.baseClip ?? DEFAULT_BASE_CLIP_URL;
  const baseTime = opts.baseTime ?? 0;
  const slots = opts.onlySlots ?? ARM_SLOTS_BOTH;
  const log = opts.log !== false;

  if (log) {
    console.log(`[extract-pose] target: ${opts.clip} @ ${opts.time}s`);
    console.log(`[extract-pose] base:   ${baseClipUrl} @ ${baseTime}s`);
  }

  const [targetClip, baseClip] = await Promise.all([
    loadClip(opts.clip),
    loadClip(baseClipUrl),
  ]);

  if (log) {
    console.log(
      `[extract-pose] target clip "${targetClip.name}" duration=${targetClip.duration.toFixed(2)}s ` +
        `tracks=${targetClip.tracks.length}`,
    );
  }

  const targetPose = evaluateClipPose(targetClip, opts.time);
  const basePose = evaluateClipPose(baseClip, baseTime);

  let pose: BonePose = {};
  for (const slot of slots) {
    const t = targetPose[slot];
    const b = basePose[slot];
    if (!t) continue;
    const baseE = b ?? new Euler(0, 0, 0, 'XYZ');
    const dx = t.x - baseE.x;
    const dy = t.y - baseE.y;
    const dz = t.z - baseE.z;
    // Drop axes that didn't change meaningfully (cleaner output).
    const offset: EulerOffset = {};
    if (Math.abs(dx) > 0.01) offset.x = round3(dx);
    if (Math.abs(dy) > 0.01) offset.y = round3(dy);
    if (Math.abs(dz) > 0.01) offset.z = round3(dz);
    if (Object.keys(offset).length > 0) pose[slot] = offset;
  }

  if (opts.mirror) {
    pose = mirrorBonePose(pose, opts.mirror);
  }

  if (log) console.log('[extract-pose] result:\n' + formatPoseAsCode(pose));
  return { pose, duration: targetClip.duration };
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Mirror an arm-bones BonePose across the body's sagittal plane. Same
 * convention as the tuning panel's mirror buttons:
 *   x stays the same  (forward/back axis is symmetric)
 *   y, z negate       (sideways and twist flip)
 *
 * Semantics: "LR" = take the LEFT side data and produce a both-sides
 * pose where the right is mirrored from the left (overwriting any prior
 * right-side data); the original left side passes through unchanged.
 * "RL" is the symmetric case. This makes one-sided source clips like
 * `Right Hand Behind Head` produce a clean both-sides pose with a
 * single checkbox toggle. Only arm chains are mirrored — legs/spine
 * pass through untouched.
 */
function mirrorBonePose(pose: BonePose, direction: 'LR' | 'RL'): BonePose {
  const out: BonePose = {};
  // Pass-through any non-arm bones already in the input.
  for (const slot of Object.keys(pose) as BoneSlot[]) {
    if (!ARM_SLOTS_BOTH.includes(slot)) {
      out[slot] = { ...pose[slot]! };
    }
  }
  for (const [l, r] of MIRROR_PAIRS) {
    const srcSlot = direction === 'LR' ? l : r;
    const dstSlot = direction === 'LR' ? r : l;
    const src = pose[srcSlot];
    if (!src) {
      // Source side empty — fall back to the destination side if it has
      // anything (preserves user-visible state when the picked clip
      // happens to populate the "wrong" side for the chosen direction).
      const fallback = pose[dstSlot];
      if (fallback) out[dstSlot] = { ...fallback };
      continue;
    }
    out[srcSlot] = { ...src };
    out[dstSlot] = stripUndefined({
      x: src.x,
      y: src.y != null ? -src.y : undefined,
      z: src.z != null ? -src.z : undefined,
    });
  }
  return out;
}

function stripUndefined(o: EulerOffset): EulerOffset {
  const r: EulerOffset = {};
  if (o.x !== undefined) r.x = o.x;
  if (o.y !== undefined) r.y = o.y;
  if (o.z !== undefined) r.z = o.z;
  return r;
}

// ── Code formatting ──────────────────────────────────────────────────────

/** Format a BonePose as TS code in body-profiles.ts style. */
export function formatPoseAsCode(pose: BonePose): string {
  const lines: string[] = ['{'];
  for (const slot of ALL_BONE_SLOTS) {
    const o = pose[slot];
    if (!o) continue;
    const x = o.x ?? 0, y = o.y ?? 0, z = o.z ?? 0;
    if (x === 0 && y === 0 && z === 0) continue;
    const parts: string[] = [];
    if (x !== 0) parts.push(`x: ${x.toFixed(3)}`);
    if (y !== 0) parts.push(`y: ${y.toFixed(3)}`);
    if (z !== 0) parts.push(`z: ${z.toFixed(3)}`);
    lines.push(`  ${slot}: { ${parts.join(', ')} },`);
  }
  lines.push('}');
  return lines.join('\n');
}
